import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  Ban,
  Banknote,
  Check,
  ChevronRight,
  Clock,
  Copy,
  CreditCard,
  Headphones,
  Home,
  ImageOff,
  Leaf,
  MapPin,
  Navigation,
  Package,
  PackageCheck,
  PhoneCall,
  RotateCcw,
  Settings,
  Smartphone,
  Truck,
  Wallet,
  X,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import DecorLeaf from '../components/DecorLeaf';
import ErrorState from '../components/ErrorState';
import DeliveryTrackingMap from '../components/DeliveryTrackingMap';
import Modal from '../components/Modal';
import usePageMetadata from '../hooks/usePageMetadata';
import * as ordersApi from '../api/endpoints/orders';
// Canonical status labels/icons and the progress builder, shared with the
// orders list so the two screens can never interpret the same fulfilment data
// differently.
import {
  buildOrderProgress,
  isTerminalFailure,
  resolveOrderStatus,
  statusLabel,
  statusTone,
} from '../config/orderStatus';
import { STORE, APP_STORE_LINKS } from '../config/store';
import { focusRing, homeRail } from '../styles/ui';

// Refresh cadence for the driver marker while an order is out for delivery.
// Comfortably under the backend's 120s staleness window so the marker is
// replaced well before the fix it shows would be considered stale.
const TRACKING_POLL_MS = 15000;

// Mirrors DELIVERY_TRACKING_STALE_AFTER_SECONDS in the backend's
// deliveryTrackingService.js; only used when a tracking payload arrives
// without its own `staleAfterSeconds`.
const DEFAULT_TRACKING_STALE_AFTER_SECONDS = 120;

// Labels/icons for the paymentMethod values the customer-app order API
// accepts ('online' | 'cod', plus fully wallet-settled orders).
const PAYMENT_METHOD_META = {
  cod: { label: 'Cash on Delivery', icon: Banknote },
  online: { label: 'Online Payment', icon: Smartphone },
  wallet: { label: 'Wallet', icon: Wallet },
};

// Sale.paymentStatus enum (see backend models/Sale.js).
const PAYMENT_STATUS_LABEL = {
  pending: 'Pending',
  partial: 'Partially Paid',
  completed: 'Paid',
  processing: 'Confirming payment…',
  refunded: 'Refunded',
  refund_pending: 'Refund in progress',
};

const PAYMENT_STATUS_TONE = {
  completed: 'text-success-700 bg-success-50 border-success-100',
  refunded: 'text-red-700 bg-red-50 border-red-100',
  refund_pending: 'text-red-700 bg-red-50 border-red-100',
};

// Presentation-only banner copy, chosen from the canonical status. It never
// tells a customer their order is being prepared when it is not.
const BANNER_COPY = {
  placed: { title: 'Thank you for your order!', body: "We've received your order and will confirm it shortly." },
  confirmed: { title: 'Thank you for your order!', body: "Your order is confirmed and will be prepared soon." },
  preparing: { title: 'Thank you for your order!', body: "We're preparing your fresh items with care." },
  packed: { title: 'Thank you for your order!', body: "We're preparing your fresh items with care." },
  allocated: { title: 'Thank you for your order!', body: "We're preparing your fresh items with care." },
  partially_allocated: { title: 'Thank you for your order!', body: "We're preparing your fresh items with care." },
  assigned: { title: 'Your order is on its way', body: 'It has been handed to a delivery partner.' },
  out_for_delivery: { title: 'Your order is on its way', body: 'It should reach you shortly.' },
  delivered: { title: 'Your order was delivered', body: 'We hope everything arrived fresh.' },
  cancelled: { title: 'This order was cancelled', body: 'No further action is needed for this order.' },
  failed: { title: "We couldn't complete this order", body: 'Please contact support if you need help.' },
};

// Brand promises already used on the site — no "trusted by" counts.
const TRUST_STRIP = [
  { icon: Leaf, title: '100% Fresh', subtitle: 'Handpicked with care' },
  { icon: PackageCheck, title: 'Hygienically Packed', subtitle: "For your family's safety" },
  { icon: Truck, title: 'Same-Day Delivery', subtitle: 'At your doorstep' },
  { icon: BadgeCheck, title: 'Quality Checked', subtitle: 'Inspected before dispatch' },
];

const formatCurrency = (value) => (value != null ? `₹${value}` : '₹0');

const formatDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
};

const formatStepTime = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return {
    date: date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }),
    time: date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }),
  };
};

// How old the driver's last position fix is *right now*. The backend stamps
// `ageSeconds` when it responds, so prefer recomputing from `updatedAt` and
// fall back to ageSeconds plus the time since we fetched it. null means
// "unknown", which callers treat as stale — matching the backend's own rule.
const getTrackingAgeSeconds = (tracking, checkedAt) => {
  const location = tracking?.driverLocation;
  if (!location) return null;
  if (location.updatedAt) {
    const updatedAt = new Date(location.updatedAt).getTime();
    if (!Number.isNaN(updatedAt)) return Math.max(0, Math.floor((Date.now() - updatedAt) / 1000));
  }
  if (typeof location.ageSeconds === 'number') {
    const sinceFetch = checkedAt ? Math.max(0, Math.floor((Date.now() - checkedAt) / 1000)) : 0;
    return location.ageSeconds + sinceFetch;
  }
  return null;
};

const Card = ({ title, subtitle, icon: Icon, action, children, className = '' }) => (
  <section className={`bg-white rounded-2xl border border-[#E6EEE6] shadow-[0_6px_18px_-10px_rgba(15,51,24,0.25)] p-5 sm:p-6 ${className}`}>
    {title && (
      <div className="flex items-start justify-between gap-3 mb-4">
        <h2 className="flex items-center gap-3 serif text-[20px] xl:text-[22px] font-bold text-emerald-950 leading-tight">
          {Icon && (
            <span className="w-[42px] h-[42px] rounded-full bg-[#E4F3E5] text-emerald-700 flex items-center justify-center shrink-0">
              <Icon size={20} aria-hidden="true" />
            </span>
          )}
          <span>
            {title}
            {subtitle && <span className="block text-[12.5px] font-sans font-normal text-ink-secondary mt-[2px] tracking-normal">{subtitle}</span>}
          </span>
        </h2>
        {action}
      </div>
    )}
    {children}
  </section>
);

const OrderProgress = ({ order }) => {
  const progress = buildOrderProgress(order);

  if (progress.failed) {
    // Terminal orders never render future stages — same rule as the list.
    return (
      <div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="w-10 h-10 rounded-full bg-red-100 text-red-700 flex items-center justify-center shrink-0">
            <X size={20} aria-hidden="true" />
          </span>
          <span className="text-lg font-bold text-red-700">{progress.label}</span>
          {progress.at && (
            <span className="text-[15px] text-ink-secondary">{formatDateTime(progress.at)}</span>
          )}
        </div>
        {progress.steps.length > 0 && (
          <p className="text-[15px] text-ink-secondary mt-3">
            Progress before it stopped: {progress.steps.map((step) => step.label).join(' · ')}.
          </p>
        )}
      </div>
    );
  }

  return (
    <ol className="flex items-start py-2" aria-label="Order progress">
      {progress.steps.map((step, index) => {
        const done = step.state === 'complete';
        const current = step.state === 'current';
        const stamp = formatStepTime(step.at);
        return (
          <li key={step.id} className="flex-1 min-w-0 flex flex-col items-center relative px-0.5">
            {index > 0 && (
              <span
                className={`absolute top-[17px] sm:top-[22px] right-1/2 left-[-50%] h-[2px] rounded-full ${
                  done ? 'bg-[#2E8B47]' : 'bg-[#E3E8E3]'
                }`}
                aria-hidden="true"
              />
            )}
            <span
              className={`relative z-10 w-9 h-9 sm:w-[46px] sm:h-[46px] rounded-full flex items-center justify-center shrink-0 ${
                done
                  ? 'bg-[#2E8B47] text-white shadow-[0_6px_14px_-6px_rgba(46,139,71,0.7)]'
                  : current
                  ? 'bg-white border-[3px] border-[#2E8B47] text-emerald-700'
                  : 'bg-[#F4F7F3] border border-[#E3E8E3] text-gray-300'
              }`}
            >
              {done ? <Check size={18} className="sm:hidden" aria-hidden="true" /> : <step.icon size={17} className="sm:hidden" aria-hidden="true" />}
              {done ? <Check size={22} strokeWidth={2.5} className="hidden sm:block" aria-hidden="true" /> : <step.icon size={20} className="hidden sm:block" aria-hidden="true" />}
            </span>
            <span
              className={`mt-2.5 text-[10px] sm:text-[13px] leading-tight text-center break-words ${
                done || current ? 'font-semibold text-emerald-950' : 'text-gray-400'
              }`}
            >
              {step.label}
            </span>
            {/* Timestamps only where fulfillment.statusHistory actually has one. */}
            {stamp && (
              <span className="mt-1 text-[9px] sm:text-[12px] text-ink-secondary leading-snug text-center">
                {stamp.date}
                <br />
                {stamp.time}
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
};

const PageSkeleton = () => (
  <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_23rem] xl:grid-cols-[minmax(0,1fr)_413px] gap-4 animate-pulse">
    <div className="space-y-4">
      <div className="h-48 rounded-2xl bg-white border border-emerald-100" />
      <div className="h-96 rounded-2xl bg-white border border-emerald-100" />
      <div className="h-32 rounded-2xl bg-white border border-emerald-100" />
    </div>
    <div className="space-y-4">
      <div className="h-32 rounded-2xl bg-white border border-emerald-100" />
      <div className="h-40 rounded-2xl bg-white border border-emerald-100" />
      <div className="h-36 rounded-2xl bg-white border border-emerald-100" />
      <div className="h-40 rounded-2xl bg-white border border-emerald-100" />
    </div>
  </div>
);

const OrderDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: 'loading', data: null, error: null });
  const [tracking, setTracking] = useState(null);
  const [trackingPoll, setTrackingPoll] = useState({ failed: false, checkedAt: Date.now() });
  const [copied, setCopied] = useState(false);
  // Returns/exchanges aren't built into this website yet, even though the
  // backend already supports them (see order.availableActions.canReturn) —
  // this popup is the interim guidance until that flow is built here.
  const [showReturnExchangeModal, setShowReturnExchangeModal] = useState(false);

  usePageMetadata({
    title: 'Order Details | Fresh & Homely',
    description: 'View your Fresh & Homely order details and delivery status.',
    canonicalPath: `/orders/${id}`,
  });

  const load = () => {
    setState({ status: 'loading', data: null, error: null });
    ordersApi
      .getOrder(id)
      .then((response) => setState({ status: 'success', data: response.data, error: null }))
      .catch((err) => setState({ status: 'error', data: null, error: err.message }));
  };

  useEffect(load, [id]);

  // Live tracking only applies while the order is actively moving toward the
  // customer — matches the fulfillment statuses the backend itself treats as
  // trackable (see isDeliveryLiveTrackable in deliveryTrackingService.js).
  // Poll while out for delivery so the driver's marker keeps moving on the
  // map; other statuses only need the one-off fetch for the ETA/ready state.
  useEffect(() => {
    setTracking(null);
    setTrackingPoll({ failed: false, checkedAt: Date.now() });
    if (state.status !== 'success') return undefined;
    const fulfillmentStatus = state.data?.fulfillment?.status;
    if (!['out_for_delivery', 'confirmed', 'preparing', 'assigned'].includes(fulfillmentStatus)) return undefined;

    let cancelled = false;
    const poll = () => {
      ordersApi.getOrderTracking(id).then((res) => {
        if (!cancelled) {
          setTracking(res.data);
          setTrackingPoll({ failed: false, checkedAt: Date.now() });
        }
      }).catch(() => {
        if (!cancelled) setTrackingPoll({ failed: true, checkedAt: Date.now() });
      });
    };

    poll();
    const intervalId = fulfillmentStatus === 'out_for_delivery' ? setInterval(poll, TRACKING_POLL_MS) : null;
    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [state.status, state.data, id]);

  const order = state.data;
  const fulfillment = order?.fulfillment || {};
  // The order's own snapshot, never the customer's current default address.
  const deliveryAddress = order?.deliveryAddress || fulfillment.deliveryAddress || null;
  const deliverySlot = order?.deliverySlot || fulfillment.deliverySlot || null;
  const paymentMeta = order ? PAYMENT_METHOD_META[order.paymentMethod] : null;
  const currentFulfillmentStatus = order ? resolveOrderStatus(order) : 'placed';
  const trackingAgeSeconds = getTrackingAgeSeconds(tracking, trackingPoll.checkedAt);
  const trackingStaleAfterSeconds = tracking?.staleAfterSeconds || DEFAULT_TRACKING_STALE_AFTER_SECONDS;
  const trackingIsStale = Boolean(
    tracking?.driverLocation
    && (tracking.driverLocation.stale
      || trackingAgeSeconds === null
      || trackingAgeSeconds > trackingStaleAfterSeconds),
  );
  const liveTrackingAvailable = Boolean(tracking?.liveTrackingAvailable && !trackingIsStale);
  const items = order?.items || [];
  const orderNumber = order?.invoiceNumber || order?._id || '';
  const banner = BANNER_COPY[currentFulfillmentStatus];
  const failedOrder = isTerminalFailure(currentFulfillmentStatus);
  const canCancel = Boolean(order?.availableActions?.canCancel);
  const canReturn = Boolean(order?.availableActions?.canReturn);
  const slotText = deliverySlot
    ? deliverySlot.label || [deliverySlot.startTime, deliverySlot.endTime].filter(Boolean).join(' – ') || 'Slot recorded'
    : null;

  const copyOrderNumber = async () => {
    try {
      await navigator.clipboard?.writeText(orderNumber);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard unavailable (permissions, insecure context) — nothing to do.
    }
  };

  return (
    <>
      <Navbar />

      <main className="relative overflow-hidden pt-20 bg-[linear-gradient(180deg,#F5FAF2_0%,#FFFFFF_40%,#F7FBF5_100%)]">
        <div className="pointer-events-none select-none absolute inset-0" aria-hidden="true">
          <img src="/pages/order-detail-hero.webp" alt="" width="636" height="166" className="hidden xl:block absolute right-[100px] top-[82px] w-[636px]" />
          <img src="/pages/order-detail-note.webp" alt="" width="120" height="135" className="hidden xl:block absolute left-[60px] top-[100px] w-[120px]" />
          <DecorLeaf className="hidden xl:block absolute left-[30px] top-[300px] w-10 text-emerald-500/70 -rotate-[20deg]" />
          <DecorLeaf className="hidden xl:block absolute left-[50px] top-[700px] w-8 text-emerald-600/60 rotate-[26deg]" />
          <DecorLeaf className="hidden xl:block absolute right-[40px] top-[330px] w-9 text-emerald-500/70 rotate-[14deg]" />
          <DecorLeaf className="hidden xl:block absolute right-[60px] top-[620px] w-7 text-emerald-600/60 -rotate-[28deg]" />
        </div>

        <div className={`${homeRail} relative pt-[14px] pb-8 xl:px-[94px]`}>
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to="/orders"
              className={`inline-flex items-center gap-2 h-[30px] px-4 rounded-full bg-[#E4F3E5] text-[12px] font-bold text-emerald-900 hover:bg-emerald-100 ${focusRing}`}
            >
              <ArrowLeft size={14} aria-hidden="true" />
              Back to Orders
            </Link>
            <nav aria-label="Breadcrumb">
              <ol className="flex items-center gap-1.5 text-[12px] text-ink-secondary">
                <li><Link to="/" className={`inline-flex items-center gap-1 hover:text-emerald-700 ${focusRing}`}><Home size={12} aria-hidden="true" />Home</Link></li>
                <li aria-hidden="true"><ChevronRight size={12} /></li>
                <li><Link to="/orders" className={`hover:text-emerald-700 ${focusRing}`}>My Orders</Link></li>
                <li aria-hidden="true"><ChevronRight size={12} /></li>
                <li aria-current="page" className="font-medium text-emerald-800">Order Details</li>
              </ol>
            </nav>
          </div>

          {state.status === 'loading' && <div className="mt-6"><PageSkeleton /></div>}
          {state.status === 'error' && <div className="mt-6"><ErrorState message={state.error} onRetry={load} /></div>}

          {state.status === 'success' && order && (
            <>
              {/* ── Header ───────────────────────────────────────────────── */}
              <div className="mt-[10px] mb-[16px] xl:pl-[100px]">
                <div className="flex flex-wrap items-center gap-4">
                  <h1 className="serif text-[36px] xl:text-[42px] font-bold text-emerald-950 leading-[1.05]">Order Details</h1>
                  <span
                    className={`inline-flex items-center gap-1.5 h-[30px] px-4 rounded-full border text-[12.5px] font-bold ${statusTone(
                      currentFulfillmentStatus
                    )}`}
                  >
                    {failedOrder ? <X size={14} aria-hidden="true" /> : <Check size={14} aria-hidden="true" />}
                    {statusLabel(currentFulfillmentStatus)}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-[6px]">
                  <p className="text-[18px] font-bold text-emerald-900 break-all"><span aria-hidden="true">#</span>{orderNumber}</p>
                  {orderNumber && (
                    <button
                      type="button"
                      onClick={copyOrderNumber}
                      aria-label={`Copy order number ${orderNumber}`}
                      className={`w-8 h-8 rounded-lg text-emerald-700 hover:bg-emerald-50 flex items-center justify-center ${focusRing}`}
                    >
                      {copied ? <Check size={16} aria-hidden="true" /> : <Copy size={16} aria-hidden="true" />}
                    </button>
                  )}
                  {copied && <span className="text-sm font-semibold text-emerald-700">Copied</span>}
                </div>
                {/* Only the timestamp the API actually provides. */}
                {order.createdAt && (
                  <p className="text-[13.5px] text-ink-secondary mt-[4px]">
                    Placed on {formatDateTime(order.createdAt)}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_23rem] xl:grid-cols-[minmax(0,1fr)_413px] gap-4 xl:gap-[17px]">
                {/* ── Left column ────────────────────────────────────────── */}
                <div className="min-w-0 space-y-4">
                  <Card
                    title="Order Status"
                    subtitle="Track your order in real-time"
                    icon={Clock}
                    action={
                      slotText && !failedOrder ? (
                        <span className="hidden sm:flex items-center gap-2.5 rounded-xl bg-[#EAF6E6] px-3.5 py-2 shrink-0">
                          <Truck size={20} className="text-emerald-700" aria-hidden="true" />
                          <span className="leading-tight">
                            <span className="block text-[11px] font-bold text-[#1E7A3C]">Delivery slot</span>
                            <span className="block text-[13px] font-bold text-emerald-950 mt-[2px]">{`Slot: ${slotText}`}</span>
                          </span>
                        </span>
                      ) : null
                    }
                  >
                    <OrderProgress order={order} />
                  </Card>

                  {order.deliveryCode?.active && order.deliveryCode?.code && (
                    <Card title="Delivery Code" icon={PackageIconFallback}>
                      <p className="text-[15px] text-emerald-800 mb-2">
                        Share this code with your delivery driver
                      </p>
                      <p className="text-3xl xl:text-4xl font-bold text-emerald-950 tracking-widest">
                        {order.deliveryCode.code}
                      </p>
                      {order.deliveryCode.expiresAt && (
                        <p className="text-sm text-emerald-700 mt-2">
                          Expires {formatDateTime(order.deliveryCode.expiresAt)}
                        </p>
                      )}
                    </Card>
                  )}

                  {tracking && (
                    <Card title="Delivery Tracking" icon={Navigation}>
                      {liveTrackingAvailable ? (
                        <DeliveryTrackingMap
                          driverLocation={tracking.driverLocation}
                          destinationCoordinates={tracking.destinationCoordinates}
                          storeCoordinates={tracking.storeCoordinates}
                          route={tracking.route}
                        />
                      ) : (
                        <p className="text-[15px] text-ink-secondary">
                          {trackingIsStale
                            ? "We haven't had a location update from the driver recently."
                            : "A live map will appear here as soon as the driver's location comes through."}
                        </p>
                      )}
                      {tracking.eta && (
                        <p className="text-[15px] text-ink-secondary mt-3">Estimated arrival: {tracking.eta}</p>
                      )}
                      {tracking.driver?.name && (
                        <p className="text-[15px] text-ink-secondary">Driver: {tracking.driver.name}</p>
                      )}
                    </Card>
                  )}

                  <Card title={`Items (${items.length})`} subtitle="Fresh items from your order" icon={Package}>
                    <ul className="space-y-2.5" data-testid="order-items">
                      {items.map((item, index) => (
                        <li key={item._id || index} className="flex items-center gap-4 rounded-xl border border-[#EEF3EE] bg-[#FBFDFB] p-3">
                          <span className="w-[80px] h-[80px] sm:w-[92px] sm:h-[92px] rounded-xl overflow-hidden bg-[#F4F7F3] flex items-center justify-center shrink-0">
                            {item.imageUrl ? (
                              <img src={item.imageUrl} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <ImageOff size={26} className="text-emerald-300" aria-hidden="true" />
                            )}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-[15px] font-bold text-emerald-950 leading-snug break-words">
                              {item.productName}
                            </span>
                            <span className="block text-[13px] text-ink-secondary mt-1.5">
                              × {item.quantity}
                              {item.sellingUom ? ` · ${item.sellingUom}` : ''}
                            </span>
                          </span>
                          <span className="text-right shrink-0">
                            <span className="block text-[16px] font-bold text-emerald-950">
                              {formatCurrency(item.lineTotal)}
                            </span>
                            {/* Secondary line is explicitly the unit price. */}
                            {item.unitPrice != null && (
                              <span className="block text-[12px] text-ink-secondary mt-1">
                                {formatCurrency(item.unitPrice)} each
                              </span>
                            )}
                            <span className="block text-[12px] text-ink-secondary mt-1">Qty: {item.quantity}</span>
                          </span>
                        </li>
                      ))}
                    </ul>

                    <div className="rounded-xl bg-[#F6F9F5] px-4 pt-3 pb-1 mt-4 space-y-2.5 text-[13.5px]">
                      <div className="flex justify-between text-ink-secondary">
                        <span>Subtotal</span>
                        <span>{formatCurrency(order.subtotal)}</span>
                      </div>
                      {/* No rate is displayed — the API supplies an amount only. */}
                      {order.totalTax > 0 && (
                        <div className="flex justify-between text-ink-secondary">
                          <span>Tax</span>
                          <span>{formatCurrency(order.totalTax)}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-ink-secondary">
                        <span>Delivery Fee</span>
                        <span>{order.deliveryFee > 0 ? formatCurrency(order.deliveryFee) : 'Free'}</span>
                      </div>
                      {order.platformCharge > 0 && (
                        <div className="flex justify-between text-ink-secondary">
                          <span>Platform Charge</span>
                          <span>{formatCurrency(order.platformCharge)}</span>
                        </div>
                      )}
                      {order.handlingCharge > 0 && (
                        <div className="flex justify-between text-ink-secondary">
                          <span>Handling Charge</span>
                          <span>{formatCurrency(order.handlingCharge)}</span>
                        </div>
                      )}
                      {order.walletAmountUsed > 0 && (
                        <div className="flex justify-between text-ink-secondary">
                          <span>Paid via Wallet</span>
                          <span>-{formatCurrency(order.walletAmountUsed)}</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between gap-4 border-t border-[#E3ECE3] pt-3 pb-2 mt-1">
                        <span className="text-[16px] font-bold text-emerald-950">Total Amount</span>
                        <span className="text-[22px] font-bold text-emerald-950">
                          {formatCurrency(order.grandTotal)}
                        </span>
                      </div>
                    </div>
                  </Card>

                  {/* Only actions this application can genuinely perform.
                      There is no reorder or invoice-download implementation in
                      the web app, so neither button is rendered. */}
                  {(canCancel || canReturn) && (
                    <Card title="Order Actions" subtitle="Need to make changes to your order?" icon={Settings}>
                      <div className="flex flex-col sm:flex-row gap-3">
                        {canCancel && (
                          <button
                            type="button"
                            onClick={() => navigate(`/orders/${id}/cancel`, { state: { order } })}
                            className={`flex-1 inline-flex items-center justify-center gap-2.5 h-[44px] px-6 rounded-xl border border-red-300 bg-red-50/60 text-red-600 font-bold text-[14px] hover:bg-red-50 transition-colors ${focusRing}`}
                          >
                            <Ban size={18} aria-hidden="true" />
                            Cancel Order
                          </button>
                        )}
                        {canReturn && (
                          <button
                            type="button"
                            onClick={() => setShowReturnExchangeModal(true)}
                            className={`flex-1 inline-flex items-center justify-center gap-2.5 h-[44px] px-6 rounded-xl border border-[#DCE8DC] bg-white text-emerald-900 font-bold text-[14px] hover:bg-emerald-50 transition-colors ${focusRing}`}
                          >
                            <RotateCcw size={18} aria-hidden="true" />
                            Return / Exchange
                          </button>
                        )}
                      </div>
                    </Card>
                  )}
                </div>

                {/* ── Right column ───────────────────────────────────────── */}
                <div className="min-w-0 space-y-4">
                  {banner && (
                    <div
                      className={`relative overflow-hidden rounded-2xl border p-5 ${
                        failedOrder ? 'bg-red-50/70 border-red-100' : 'bg-[#EAF6E6] border-[#DFF3DA]'
                      }`}
                    >
                      <div className="relative flex items-start gap-3">
                        <span className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${failedOrder ? 'bg-red-600 text-white' : 'bg-[#2E8B47] text-white shadow-[0_6px_14px_-6px_rgba(46,139,71,0.7)]'}`}>
                          {failedOrder ? <X size={22} aria-hidden="true" /> : <Check size={24} strokeWidth={3} aria-hidden="true" />}
                        </span>
                        <span className="min-w-0">
                          <p className={`serif text-[18px] font-bold leading-tight ${failedOrder ? 'text-red-800' : 'text-emerald-950'}`}>
                            {banner.title}
                          </p>
                          <p className="text-[13px] text-[#4B5563] mt-1.5 leading-relaxed">{banner.body}</p>
                        </span>
                      </div>
                    </div>
                  )}

                  {/* The order's own address snapshot. No Change control: the
                      customer API has no post-order address-change endpoint. */}
                  <Card title="Delivery Address" icon={Home}>
                    {deliveryAddress ? (
                      <>
                        <address className="text-[13.5px] text-[#4B5563] leading-[1.6] not-italic break-words">
                          {[
                            deliveryAddress.line1,
                            deliveryAddress.line2,
                            deliveryAddress.city,
                            deliveryAddress.state,
                            deliveryAddress.pincode,
                          ]
                            .filter(Boolean)
                            .join(', ')}
                        </address>
                        {deliveryAddress.landmark && (
                          <p className="mt-2.5 inline-flex items-center gap-1.5 rounded-lg bg-[#F1FAEF] px-3 py-1.5 text-[12px] text-emerald-900">
                            <MapPin size={13} className="text-emerald-700" aria-hidden="true" />
                            Landmark: {deliveryAddress.landmark}
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-[14px] text-ink-secondary">
                        No delivery address was recorded for this order.
                      </p>
                    )}
                  </Card>

                  <Card title="Delivery Time Slot" icon={Clock}>
                    {deliverySlot ? (
                      <>
                        <p className="text-[16px] font-bold text-emerald-950 break-words">{slotText}</p>
                        {/* `selectedAt` is a real field on the slot snapshot. */}
                        {deliverySlot.selectedAt && (
                          <p className="text-[12.5px] text-ink-secondary mt-1.5">
                            Selected {formatDateTime(deliverySlot.selectedAt)}
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-[14px] text-ink-secondary">
                        No delivery slot was recorded for this order.
                      </p>
                    )}
                  </Card>

                  <Card
                    title="Payment Method"
                    icon={paymentMeta?.icon || CreditCard}
                    action={
                      order.paymentStatus ? (
                        <span
                          className={`inline-flex items-center h-[28px] px-3.5 rounded-full border text-[12px] font-bold shrink-0 ${
                            PAYMENT_STATUS_TONE[order.paymentStatus] ||
                            'text-[#B45309] bg-[#FFF7E6] border-[#FDE6B8]'
                          }`}
                        >
                          {PAYMENT_STATUS_LABEL[order.paymentStatus] || order.paymentStatus}
                        </span>
                      ) : null
                    }
                  >
                    <p className="text-[15px] font-semibold text-emerald-950 break-words -mt-1">
                      {paymentMeta?.label || order.paymentMethod || 'Not specified'}
                    </p>
                  </Card>

                  <div className="relative overflow-hidden rounded-2xl bg-[linear-gradient(135deg,#F1FAEF_0%,#E4F3E5_100%)] border border-[#DFF3DA] p-5 min-h-[190px]">
                    <img src="/pages/order-help-agent.webp" alt="" aria-hidden="true" loading="lazy" width="206" height="194" className="pointer-events-none absolute right-0 bottom-0 h-[170px] w-auto hidden sm:block" />
                    <div className="relative max-w-[62%]">
                      <p className="serif text-[20px] font-bold text-emerald-950 leading-tight flex items-center gap-2">
                        <Headphones size={20} className="text-emerald-700" aria-hidden="true" />
                        Need Help?
                      </p>
                      <p className="text-[12.5px] text-[#4B5563] mt-2 leading-relaxed">
                        Have questions about your order? Our team is here to help.
                      </p>
                      <Link
                        to="/support"
                        className={`mt-4 inline-flex items-center gap-2 h-[38px] px-5 rounded-full bg-[linear-gradient(90deg,#2E8B47,#1E7A3C)] text-white text-[13px] font-bold shadow-[0_6px_14px_-6px_rgba(46,139,71,0.6)] ${focusRing}`}
                      >
                        Contact Support
                        <ArrowRight size={15} aria-hidden="true" />
                      </Link>
                      <a
                        href={STORE.phoneHref}
                        className={`mt-2.5 inline-flex items-center gap-2 h-[38px] px-5 rounded-full bg-white border border-[#2E8B47] text-[#1E5A30] text-[13px] font-bold hover:bg-emerald-50 ${focusRing}`}
                      >
                        <PhoneCall size={15} aria-hidden="true" />
                        Call {STORE.phone}
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <Modal
                open={showReturnExchangeModal}
                onClose={() => setShowReturnExchangeModal(false)}
                title="Return or Exchange"
              >
                <div className="p-6 flex flex-col items-center text-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center">
                    <Smartphone size={26} className="text-emerald-700" aria-hidden="true" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-ink-primary leading-relaxed">
                      Returns &amp; exchanges are easier in the FreshnHomely Daily app.
                    </p>
                    <p className="text-xs text-ink-secondary leading-relaxed">
                      Download the app to request and manage your return or exchange.
                    </p>
                  </div>

                  {(APP_STORE_LINKS.android || APP_STORE_LINKS.ios) && (
                    <div className="w-full flex flex-col sm:flex-row gap-2.5">
                      {APP_STORE_LINKS.android && (
                        <a
                          href={APP_STORE_LINKS.android}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Get it on Google Play"
                          className="flex-1 flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-ink-primary shadow-sm hover:bg-gray-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-700 focus-visible:ring-offset-2"
                        >
                          <svg viewBox="0 0 24 24" className="w-5 h-5 shrink-0" aria-hidden="true" fill="currentColor">
                            <path d="M3.609 1.814 13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92Zm10.89 9.479 2.702-2.702-9.603-5.52 6.901 8.222Zm0 1.414-6.9 8.222 9.602-5.52-2.702-2.702ZM19.1 10.74l-2.452 1.41 2.452 1.41 1.36-.783a.996.996 0 0 0 0-1.254l-1.36-.783Z" />
                          </svg>
                          Get it on Google Play
                        </a>
                      )}
                      {APP_STORE_LINKS.ios && (
                        <a
                          href={APP_STORE_LINKS.ios}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Download on the App Store"
                          className="flex-1 flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-ink-primary shadow-sm hover:bg-gray-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-700 focus-visible:ring-offset-2"
                        >
                          <svg viewBox="0 0 24 24" className="w-5 h-5 shrink-0" aria-hidden="true" fill="currentColor">
                            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11Z" />
                          </svg>
                          Download on the App Store
                        </a>
                      )}
                    </div>
                  )}

                  {(APP_STORE_LINKS.android || APP_STORE_LINKS.ios) && (
                    <p className="text-[11px] text-ink-tertiary">
                      Available on Android and iPhone
                    </p>
                  )}

                  <button
                    type="button"
                    onClick={() => setShowReturnExchangeModal(false)}
                    className="w-full rounded-full border border-gray-200 text-ink-secondary font-medium py-2.5 text-sm hover:bg-gray-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-700 focus-visible:ring-offset-2"
                  >
                    Maybe Later
                  </button>
                </div>
              </Modal>
            </>
          )}
        </div>

        {/* Trust strip */}
        <div className="border-t border-[#EEF3EE] bg-white">
          <div className={homeRail}>
            <ul className="grid grid-cols-2 xl:grid-cols-4 gap-4 xl:gap-0 xl:divide-x xl:divide-[#E3ECE3] py-[14px] xl:px-[190px]">
              {TRUST_STRIP.map(({ icon: Icon, title, subtitle }) => (
                <li key={title} className="flex items-center gap-3 xl:px-6 first:xl:pl-0">
                  <span className="w-10 h-10 rounded-full bg-[#E4F3E5] text-emerald-700 flex items-center justify-center shrink-0">
                    <Icon size={19} strokeWidth={1.9} aria-hidden="true" />
                  </span>
                  <span className="leading-tight">
                    <span className="block text-[13px] font-bold text-emerald-950">{title}</span>
                    <span className="block text-[11.5px] text-ink-secondary mt-[3px]">{subtitle}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
};

// Small alias so the delivery-code card reuses the truck glyph already imported.
function PackageIconFallback(props) {
  return <Truck {...props} />;
}

export default OrderDetailPage;
